<template>
  <Band container="xs">
    <div>
      <Icon
        icon="message-smile"
        prefix="far"
        class="text-5xl text-primary lg:absolute lg:-left-20"
      />
      <Heading
        type="h1"
        variant="h2"
        :ui="{ base: 'mt-4' }"
      >
        {{ $t("recommend_us_to_suppliers") }}
      </Heading>
      <LeadParagraph class="mt-3">
        {{ $t("recommend_us_description") }}
      </LeadParagraph>
    </div>

    <div class="mt-12">
      <div>
        <div class="relative rounded-2xl border-b-2 border-primary-50 border-b-primary-100 bg-[#f7fbfd] p-8 text-primary-400">
          <Icon
            icon="quote-left"
            class="absolute -left-1 -top-1 -rotate-12 text-lg text-primary-300"
          />
          <span
            v-if="status === 'pending'"
            class="flex items-center space-x-3"
          >
            <Icon
              icon="spinner-third"
              prefix="fad"
              spin
              class="text-primary-300"
            />
            <p>{{ $t("bilbert_email_start") }} <span class="loading-dots" /></p>
          </span>
          <p v-else>
            {{ $t("bilbert_email_end") }}
          </p>
          <Button
            :prepend-icon="buttonIcon.icon"
            :prepend-icon-prefix="buttonIcon.prefix"
            :disabled="status !== 'success'"
            class="mt-4"
            size="sm"
            @click="copy()"
          >
            <span v-if="status === 'pending'">{{ $t("just_a_moment") }}</span>
            <span v-else-if="copied">{{ $t("copied") }}!</span>
            <span v-else>{{ $t("copy_and_paste_in_email") }}</span>
          </Button>
        </div>
        <div class="mx-6 mt-4 flex items-center space-x-3 text-sm font-medium text-primary">
          <Icon
            icon="robot"
            prefix="far"
            class="-rotate-12 text-2xl text-primary"
          />
          <span>Bilbert <span class="text-xs font-normal text-supplement">&mdash; Approach Guides' {{ $t("ai_robot") }}</span></span>
        </div>
      </div>

      <AiDraft
        v-model="message"
        :endpoint="`ai/${locale}/recommend-to-suppliers`"
        :language="locale"
        class="mt-12 bg-white"
        @status="onStatusChange"
      />

      <Button
        :prepend-icon="buttonIcon.icon"
        :prepend-icon-prefix="buttonIcon.prefix"
        :disabled="status !== 'success'"
        class="mt-8 lg:hidden"
        @click="copy()"
      >
        <span v-if="status === 'pending'">{{ $t("just_a_moment") }}</span>
        <span v-else-if="copied">{{ $t("copied") }}!</span>
        <span v-else>{{ $t("copy_and_paste_in_email") }}</span>
      </Button>
    </div>
  </Band>
</template>

<script setup lang="ts">
import type { AiGenerationStatus } from '~/types/ai'

const { locale } = useI18n()

const message = ref()

// AI Pending
const status = ref<AiGenerationStatus>('idle')
const onStatusChange = (value: boolean) => (status.value = value)

// Copy AI message
const { copy, copied } = useClipboard({ source: message })

// Button icon
const buttonIcon = computed(() => {
  if (!status.value) {
    return { icon: 'copy', prefix: 'fas' }
  } else if (copied.value) {
    return { icon: 'check', prefix: 'fas' }
  }

  return ''
})
</script>

<style>
@keyframes dot-animation {
  0% {
    content: "";
  }
  25% {
    content: ".";
  }
  50% {
    content: "..";
  }
  75% {
    content: "...";
  }
  100% {
    content: "";
  }
}

.loading-dots::after {
  content: "";
  animation: dot-animation 1.25s infinite;
}
</style>
